import { createFileRoute } from "@tanstack/react-router";
import { PageHero } from "@/components/PageHero";
import { CTASection } from "@/components/CTASection";
import { SITE } from "@/lib/site";
import { buildBreadcrumbs } from "@/components/SchemaJsonLd";
import flakeImg from "@/assets/flake-closeup.jpg";

export const Route = createFileRoute("/terms-of-service")({
  head: () => ({
    meta: [
      { title: "Terms of Service | MLC Epoxy Floors" },
      { name: "description", content: "Terms and Conditions for MLC Epoxy Floors messaging programs and services." },
      { property: "og:title", content: "Terms of Service | MLC Epoxy Floors" },
      { property: "og:description", content: "Terms and Conditions for our messaging programs and services." },
      { property: "og:url", content: "/terms-of-service" },
    ],
    links: [{ rel: "canonical", href: "/terms-of-service" }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(buildBreadcrumbs([{ name: "Home", href: "/" }, { name: "Terms of Service", href: "/terms-of-service" }])) },
    ],
  }),
  component: TermsOfServicePage,
});

function TermsOfServicePage() {
  return (
    <>
      <PageHero
        eyebrow="Legal"
        title="Terms of Service"
        body="Terms and Conditions for our messaging programs and services."
        image={flakeImg}
      />
      <section className="container-x py-20">
        <article className="mx-auto max-w-3xl prose prose-invert prose-headings:font-display prose-headings:uppercase prose-headings:text-silver-light prose-a:text-copper hover:prose-a:text-copper-hover prose-strong:text-silver-light text-silver">
          <p className="text-sm text-muted-foreground">Last updated: June 1, 2026</p>

          <h2>1. Introduction</h2>
          <p>These Terms and Conditions govern your use of the SMS and email messaging programs operated by MLC Epoxy Floors (“we,” “our,” or “us”). By opting into our communications, you agree to these Terms and our Privacy Policy.</p>

          <h2>2. Program Description (SMS Use Cases)</h2>
          <p>By opting into our messaging program, you may receive SMS and email messages from MLC Epoxy Floors related to the services you have requested or consented to receive. Messages may include:</p>
          <ul>
            <li>Appointment confirmations and reminders</li>
            <li>Service updates</li>
            <li>Transactional and informational messages</li>
            <li>Customer support responses</li>
            <li>Marketing and promotional messages (only where consent is provided)</li>
          </ul>
          <p>Message frequency may vary.</p>

          <h2>3. Consent to Receive Messages &amp; Age Requirement</h2>
          <p>By providing your phone number and opting into our messaging program, you authorize MLC Epoxy Floors to send SMS messages to your mobile number in accordance with these Terms and our Privacy Policy.</p>
          <p>You must be at least 18 years of age to participate in our messaging program.</p>
          <p>Consent is not a condition of purchase.</p>

          <h2>4. Opt-Out Instructions</h2>
          <p>You may opt out of receiving SMS messages at any time by replying STOP to any message you receive from us.</p>
          <p>After you send STOP, you will receive a confirmation message indicating that you have been unsubscribed. You will no longer receive SMS messages from us unless you opt in again.</p>

          <h2>5. Opt-In Again</h2>
          <p>If you wish to rejoin our messaging program after opting out, you may opt in again using the same method you originally used to enroll through the contact form on our website at <a href="https://mlcepoxy.ca">mlcepoxy.ca</a>.</p>

          <h2>6. Help and Customer Support</h2>
          <p>If you need assistance or are experiencing issues with our messaging program, you may reply HELP to any message, or contact us directly using the information below:</p>
          <ul>
            <li>Email: <a href={`mailto:${SITE.email}`}>{SITE.email}</a></li>
            <li>Phone: <a href={SITE.phoneHref}>{SITE.phone}</a></li>
          </ul>

          <h2>7. Message Frequency and Rates</h2>
          <p>Message frequency may vary depending on your interaction with our services.</p>
          <p>Message and data rates may apply for messages sent to and from you. Please consult your wireless carrier for details regarding your messaging and data plan.</p>

          <h2>8. Carrier Liability Disclaimer</h2>
          <p>Wireless carriers are not liable for delayed or undelivered messages.</p>

          <h2>9. Privacy and Data Use</h2>
          <p>Your participation in our messaging program is governed by our Privacy Policy, which explains how we collect, use, protect, and retain your personal information.</p>
          <p>You can view our Privacy Policy here: <a href="/privacy-policy">/privacy-policy</a></p>

          <h2>10. Mobile Information Sharing Statement (A2P Required)</h2>
          <p>No mobile information will be shared with third parties or affiliates for marketing or promotional purposes.</p>
          <p>Information sharing is limited to service providers assisting with messaging delivery and customer communication, all of whom are required to protect your data and use it only for authorized purposes.</p>

          <h2>11. Compliance with A2P 10DLC</h2>
          <p>Our messaging practices comply with A2P 10DLC regulations. All communications are conducted in accordance with applicable carrier guidelines, industry standards, and legal requirements.</p>

          <h2>12. Changes to These Terms</h2>
          <p>We may update these Terms of Service from time to time to reflect changes in our services, business practices, or legal requirements. Any updates will be posted with a revised “Last Updated” date. Continued participation in our messaging program constitutes acceptance of the updated Terms.</p>

          <h2>13. Contact Us</h2>
          <p>If you have any questions, concerns, or requests regarding these Terms and Conditions or how your personal information is handled, please contact us at:</p>
          <p>
            <strong>MLC Epoxy Floors</strong><br />
            {SITE.address.street}, {SITE.address.city}, {SITE.address.region} {SITE.address.postal}<br />
            <a href={SITE.phoneHref}>{SITE.phone}</a><br />
            <a href={`mailto:${SITE.email}`}>{SITE.email}</a>
          </p>
        </article>
      </section>
      <CTASection />
    </>
  );
}
